/**
 * Red lipstick shades — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_nails_shades`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const redLipstickShadesPack: ToilePack = {
    slug: 'red-lipstick-shades',
    category: 'beauty',
    name: 'Red lipstick shades',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'cherry', label: 'Cherry red', subject: 'a close lip swatch in a bright cherry red cream finish'},
        {key: 'brick', label: 'Brick red', subject: 'a close lip swatch in a warm brick red matte finish'},
        {key: 'bluered', label: 'Blue red', subject: 'a close lip swatch in a cool blue-red satin finish'},
    ],
    styles: [
        {key: 'fair', label: 'Fair skin', style: 'worn on fair skin with cool pink undertones, soft daylight from camera-left, a bright even balance', palette: 'Pale porcelain skin against a plain light grey ground, the red the only saturated colour', framing: 'A near-frontal tight crop of the lips and chin, centred in a vertical frame'},
        {key: 'medium', label: 'Medium skin', style: 'worn on medium olive skin with warm undertones, soft daylight from camera-right, a warm natural balance', palette: 'Golden olive skin against a plain warm beige ground', framing: 'A near-frontal tight crop of the lips and chin, centred in a vertical frame'},
        {key: 'deep', label: 'Deep skin', style: 'worn on deep brown skin with warm undertones, soft daylight from a near-frontal window, a rich balance with a gentle sheen on the skin', palette: 'Rich brown skin against a plain soft taupe ground', framing: 'A near-frontal tight crop of the lips and chin, centred in a vertical frame'},
    ],
    directions: ['nude lipstick', 'pink lipstick', 'glossy finish'],
    directionHint: 'try: nude lipstick',
    seo: {
        title: 'Red Lipstick Shades: Cherry, Brick, Blue-Red',
        metaDescription: 'Nine red lipstick shade pictures: cherry, brick and blue-red swatches, each worn on fair, medium and deep skin.',
        h1: 'Red lipstick shades: cherry, brick, blue-red',
        tagline: 'Nine lip swatches: three reds, each worn on three skin tones.',
        intro: 'A shade guide for red lipstick, by name and undertone. The nine pictures are lip swatches of a bright cherry cream, a warm brick matte and a cool blue-red satin. Each red is worn on fair skin with cool pink undertones, medium olive skin and deep brown skin with warm undertones, so you can compare one red across skin tones. Type a different finish or a twist, such as glossy finish or pink lipstick, and the same settings carry it.',
        useCases: [
            'a shade check before you buy a red lipstick',
            "a makeup artist's shade reference",
            'a red lipstick idea for a holiday or a party',
            'a comparison of undertones for your own skin',
        ],
        faq: [
            {q: 'What are the three reds?', a: 'Cherry is a bright cream finish, brick is a warm matte, and blue-red is a cool satin.'},
            {q: 'How are the skin tones chosen?', a: 'Fair skin with cool pink undertones, medium olive skin with warm undertones, and deep brown skin with warm undertones.'},
            {q: 'Does the pack give shade names from brands?', a: 'No. The three reds are named by their tone and finish, not by a brand or a product name.'},
            {q: 'Can I ask for a different red?', a: 'Yes. Type the change as a request. The picture is not promised to match it exactly.'},
        ],
    },
}
